/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 툴바 컴포넌트 클래스를 정의한다.
 *
 * @file /src/components/toolbar/Toolbar.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 6. 21.
 */
import * as parent from '@base/Component';
import { Item } from '@components/toolbar/item/Item';
import { Fill } from '@components/toolbar/item/fill/Fill';
import { Separator } from '@components/toolbar/item/separator/Separator';
import { LanguageText } from '@utils/Language';
import { IndicatorType, Loading } from '@elements/loading/Loading';
import './Toolbar.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 툴바 아이템
     */
    tools?: (string | parent.Component)[];

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Toolbar extends parent.Component {
    /**
     * 컴포넌트 타입
     */
    type = 'toolbar';

    /**
     * 컴포넌트 확장타입
     */
    variant = 'toolbar';

    /**
     * 툴바위치
     */
    position: 'top' | 'bottom' | 'buttons' = 'top';

    /**
     * 로딩
     */
    loading: Loading = null;

    /**
     * 툴바 컴포넌트를 정의한다.
     *
     * @param properties - 컴포넌트 초기설정
     */
    constructor(
        public properties: Pick<
            Properties<Toolbar>,
            'id' | 'tools' | 'layout' | 'hidden' | 'scrollable' | 'listeners'
        > = null
    ) {
        super(properties);

        this.scrollable = this.properties?.scrollable ?? 'x';
        this.$scrollable = this.$getContent();
        this.layout = this.properties.layout ?? 'column';
    }

    /**
     * 자식 컴포넌트를 초기화한다.
     */
    initItems(): void {
        const existingItems = this.items ?? [];

        this.items = [];
        for (const tool of this.properties.tools ?? []) {
            if (tool instanceof parent.Component) {
                tool.setParent(this);
                this.items.push(tool);
            } else {
                if (tool == '-' || tool == '|') {
                    const item = new Separator();
                    item.setParent(this);
                    this.items.push(item);
                } else if (tool == '->') {
                    const item = new Fill();
                    item.setParent(this);
                    this.items.push(item);
                } else {
                    const item = new Item({ text: tool });
                    item.setParent(this);
                    this.items.push(item);
                }
            }
        }

        this.items.push(...existingItems);
    }

    /**
     * 툴바위치를 정의한다.
     *
     * @param position - 툴바위치 (top, bottom, buttons)
     */
    setPosition(position: 'top' | 'bottom' | 'buttons'): void {
        this.position = position;

        this.$getComponent().addClass(this.position);
    }

    /**
     * 자식 컴포넌트를 추가한다.
     *
     * @param tool - 툴
     */
    async addTool(tool: string | parent.Component): Promise<void> {
        let item: parent.Component;

        if (tool instanceof parent.Component) {
            item = tool;
        } else {
            if (tool == '-' || tool == '|') {
                item = new Separator();
            } else if (tool == '->') {
                item = new Fill();
            } else {
                item = new Item({ text: tool });
            }
        }

        await this.appendItem(item);
    }

    /**
     * 로딩메시지를 세팅한다.
     *
     * @param loadingType - 로딩메시지 타입 또는 로딩메시지를 보일지 여부
     * @param loadingText - 로딩메시지
     * @returns 로딩메시지
     */
    setLoading(loadingType: IndicatorType | boolean | null = null, loadingText: LanguageText = null): void {
        const isLoading = loadingType !== false;
        const indicator = typeof loadingType == 'boolean' ? null : loadingType;

        if (isLoading === false) {
            this.loading?.hide();
            return;
        }

        if (this.loading === null) {
            this.loading = new Loading({
                indicator: indicator ?? 'column',
                direction: 'column',
                text: loadingText ?? null,
            });

            this.loading.setComponent(this);
            this.$getContainer().append(this.loading.$getElement());
        } else {
            this.loading.setIndicator(indicator ?? 'column');
            this.loading.setText(loadingText ?? null);
        }

        this.loading.show();
    }
}
